import React, { useState, useEffect } from 'react';
import Navbar from '../Navbar/Nabvar';
import Footer from '../Footer/Footer';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardMedia,
    styled,
    useMediaQuery,
    useTheme,
    Container
} from '@mui/material';
import { mock_methods } from './mock_methods';
import MethodDialog from './MethodDialog';
import QuoteBox from '../Quote/QuoteBox';

interface Method {
    id: string;
    title: string;
    color: string;
    text: string;
    image_kachel: string;
    image_download: string;
}

const MainContent = styled(Box)({
    position: 'relative',
    width: '100%',
    overflowX: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            linear-gradient(to bottom right, 
                rgba(240, 245, 255, 0.9),
                rgba(230, 240, 255, 0.9)
            )
        `,
        zIndex: 0
    }
});

const ContentWrapper = styled(Box)({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margin: '0',
    padding: '0',
});

const ContentSection = styled(Box)(({ theme }) => ({
    width: '100%',
    margin: '100px 0', // Reduced from 150px
    padding: theme.spacing(4),
    backgroundColor: '#fff',
    borderTop: '0.5px solid #2514b8',
    borderBottom: '0.5px solid #2514b8',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        margin: '60px 0',
        padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
        margin: '40px 0',
        padding: theme.spacing(2),
    }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(4, 0),
    width: '100%',
    '& img': {
        width: '100%',
        maxWidth: '900px',
        height: 'auto',
        objectFit: 'contain',
        [theme.breakpoints.down('md')]: {
            maxWidth: '100%',
        },
    },
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
        margin: theme.spacing(2, 0),
    },
}));

const CardGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: '1800px',
    padding: theme.spacing(2),
    '& .MuiCard-root': {
        width: 'calc(20% - 16px)', // Verkleinert von 25% auf 20%
        minWidth: '200px', // Verkleinert von 280px auf 200px
        aspectRatio: '1',
        backgroundColor: '#186cff',
        margin: theme.spacing(1),
        transition: 'transform 0.3s ease',
        '&:hover': {
            transform: 'scale(1.03)'
        }
    },
    [theme.breakpoints.down('lg')]: {
        '& .MuiCard-root': {
            width: 'calc(25% - 16px)', // Verkleinert von 33.333% auf 25%
            aspectRatio: '1',
        },
    },
    [theme.breakpoints.down('md')]: {
        '& .MuiCard-root': {
            width: 'calc(33.333% - 16px)', // Verkleinert von 50% auf 33.333%
            aspectRatio: '1',
        },
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        gap: theme.spacing(1),
        '& .MuiCard-root': {
            width: 'calc(50% - 8px)', // Verkleinert von 100% auf 50%
            aspectRatio: '1',
            margin: theme.spacing(1, 0),
            minWidth: 'unset',
        },
    },
}));

const MethodTitle = styled(Typography)(({ theme }) => ({
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: '1.2rem',
    wordBreak: 'break-word',
    hyphens: 'auto',
    overflowWrap: 'break-word',
    padding: '0 8px',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.1rem',
    },
}));

const ResponsiveBackground = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '800px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    [theme.breakpoints.down('lg')]: {
        height: '700px',
    },
    [theme.breakpoints.down('md')]: {
        height: '600px',
    },
    [theme.breakpoints.down('sm')]: {
        height: '400px',
    }
}));

const KnowHow: React.FC = () => {
    const primaryBlue = '#1F1B9E';
    const [selectedMethod, setSelectedMethod] = useState<Method | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const getFontSize = (base: number) => {
        if (isMobile) return `${base * 0.7}rem`;
        if (isTablet) return `${base * 0.85}rem`;
        return `${base}rem`;
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Navbar />
            <MainContent>
                <ContentWrapper>
                    <Container maxWidth="xl">
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mt: isMobile ? 2 : isTablet ? 2.5 : 3
                        }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    px: isMobile ? 1 : 2,
                                    mb: isMobile ? 2 : 3
                                }}
                            >
                                <img
                                    src="old/knowHowclear.png"
                                    alt="Logo"
                                    style={{
                                        maxHeight: isMobile ? 140 : isTablet ? 200 : 260,
                                        width: 'auto',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                        paddingTop: '70px',
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: primaryBlue,
                                    maxWidth: '1000px',
                                    mx: 'auto',
                                    px: isMobile ? 2 : 3,
                                    fontSize: getFontSize(1.6),
                                    mb: isMobile ? 2 : 3,
                                    textAlign: 'center',
                                    lineHeight: 1.4,
                                    marginBottom: '-40px',
                                }}
                            >
                                Erfolgreiche Geschäftsmodelle basieren auf der richtigen Methodik. Entdecken Sie bewährte Ansätze zur Geschäftsmodellentwicklung und nutzen Sie praxisnahe Vorlagen, um Ihre Ideen strukturiert auszuarbeiten. Mit den richtigen Tools legen Sie den Grundstein für nachhaltigen Erfolg!
                            </Typography>
                        </Box>
                    </Container>

                    {/*<Box*/}
                    {/*    sx={{*/}
                    {/*        width: '100%',*/}
                    {/*        position: 'relative',*/}
                    {/*        my: isMobile ? 2 : isTablet ? 3 : 4,*/}
                    {/*        '&::before': {*/}
                    {/*            content: '""',*/}
                    {/*            position: 'absolute',*/}
                    {/*            top: 0,*/}
                    {/*            left: 0,*/}
                    {/*            right: 0,*/}
                    {/*            height: '1px',*/}
                    {/*            backgroundColor: primaryBlue,*/}
                    {/*        },*/}
                    {/*        '&::after': {*/}
                    {/*            content: '""',*/}
                    {/*            position: 'absolute',*/}
                    {/*            bottom: 0,*/}
                    {/*            left: 0,*/}
                    {/*            right: 0,*/}
                    {/*            height: '1px',*/}
                    {/*            backgroundColor: primaryBlue,*/}
                    {/*        }*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    <ResponsiveBackground />*/}
                    {/*</Box>*/}

                    <ContentSection>
                        <Typography
                            variant="h1"
                            sx={{
                                color: primaryBlue,
                                mb: isMobile ? 2 : 4,
                                fontSize: getFontSize(3),
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            Weiterentwicklungsmethoden
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: primaryBlue,
                                maxWidth: '1000px',
                                mx: 'auto',
                                px: isMobile ? 1 : 3,
                                fontSize: getFontSize(1.3),
                                mb: isMobile ? 2 : 3,
                                textAlign: 'center'
                            }}
                        >
                            Klicken Sie auf die Kacheln, um zu den Methoden zu gelangen.
                            Eine detaillierte Beschreibung der Methoden und Anwendungen, erhalten Sie jeweils durch Download der Methode im Warenkorb.
                        </Typography>

                        <CardGrid sx={{
                            gap: isMobile ? 1 : isTablet ? 2 : 3,
                            px: isMobile ? 1 : 2
                        }}>
                            {mock_methods.map((method) => (
                                <Card
                                    key={method.id}
                                    sx={{
                                        position: 'relative',
                                        border: `2px solid ${method.color}`,
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                        display: 'flex', // Hinzufügen
                                        flexDirection: 'column' // Hinzufügen
                                    }}
                                    onClick={() => setSelectedMethod(method)}
                                >
                                    <CardMedia
                                        component="img"
                                        image={method.image_kachel || "/api/placeholder/400/320"}
                                        alt={method.title}
                                        sx={{
                                            objectFit: 'cover',
                                            flex: 1, // Hinzufügen - nimmt verfügbaren Platz ein
                                            height: 'auto' // Height entfernen und durch auto ersetzen
                                        }}
                                    />
                                    <CardContent sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        width: '100%',
                                        background: method.color,
                                        padding: isMobile ? '8px !important' : '12px !important',
                                    }}>
                                        <MethodTitle variant="h6">
                                            {method.title}
                                        </MethodTitle>
                                    </CardContent>
                                </Card>
                            ))}
                        </CardGrid>
                    </ContentSection>

                    {selectedMethod && (
                        <MethodDialog
                            open={!!selectedMethod}
                            onClose={() => setSelectedMethod(null)}
                            method={selectedMethod}
                        />
                    )}

                    <Box sx={{marginTop:"-200px"}}>
                        <QuoteBox
                            text="„Ideas are easy. Implementation is hard.“"
                            author={"Guy Kawasaki"}
                            maxWidth="1300px"
                        />
                    </Box>
                </ContentWrapper>
            </MainContent>
            <Footer />
        </>
    );
};

export default KnowHow;